import { Team } from "@redotech/redo-model/team";
import { RedoTextInput } from "@redotech/redo-web/arbiter-components/input/redo-text-input";
import { RedoToggle } from "@redotech/redo-web/arbiter-components/toggle/redo-toggle";
import { Flex } from "@redotech/redo-web/flex";
import {
  groupInput,
  input,
  InputProvider,
  numberValidator,
} from "@redotech/ui/form";
import round from "lodash/round";
import { memo } from "react";

export const yofiForm = groupInput({
  enabled: input<boolean>(),
  paymentsEnabled: input<boolean>(),
  percentage: input<string>({
    validator: numberValidator({ min: 0, max: 100 }),
  }),
  autoFlagReturns: input<boolean>(),
});

export type YofiForm = InputProvider.Form<typeof yofiForm>;
export type YofiValue = InputProvider.Value<typeof yofiForm>;

export function yofiTeamToFormValue(team: Team): YofiValue {
  return {
    enabled: team.settings.yofi?.enabled ?? false,
    paymentsEnabled: team.settings.yofi?.paymentsEnabled ?? false,
    percentage: team.settings.yofi?.percentage
      ? String(round(team.settings.yofi.percentage * 100.0, 3))
      : "20",
    autoFlagReturns: team.settings.yofi?.autoFlagReturns ?? false,
  };
}

export function yofiFormValueToTeam(value: YofiValue) {
  return {
    enabled: value.enabled,
    paymentsEnabled: value.paymentsEnabled,
    percentage: round(+value.percentage, 3) / 100.0,
    autoFlagReturns: value.autoFlagReturns,
  };
}

export const yofiDefault: YofiValue = {
  enabled: false,
  paymentsEnabled: false,
  percentage: "20",
  autoFlagReturns: false,
};

export const YofiIntegrationCard = memo(function YofiIntegrationCard({
  input,
}: {
  input: YofiForm;
}) {
  const { enabled, paymentsEnabled, percentage, autoFlagReturns } =
    input.inputs;

  return (
    <Flex dir="column" gap="md">
      <RedoToggle
        description="provides fraud risk scores for orders, helping merchants identify potentially fraudulent returns."
        label="Enable Yofi fraud risk scoring"
        setValue={enabled.setValue}
        size="sm"
        value={enabled.value}
      />

      {enabled.value && (
        <>
          <RedoToggle
            description="Automatically flag returns with high risk scores"
            label="Auto-flag returns"
            setValue={autoFlagReturns.setValue}
            size="sm"
            value={autoFlagReturns.value ?? false}
          />

          {autoFlagReturns.value && (
            <>
              <RedoToggle
                description="Enable fee collection for Yofi fraud detection"
                label="Enable Payments"
                setValue={paymentsEnabled.setValue}
                size="sm"
                value={paymentsEnabled.value ?? false}
              />
              <RedoTextInput
                description="Percentage fee charged of cancelled returns that were automatically flagged by Yofi fraud assessment"
                inputTypeHint="number"
                label="Yofi service fee"
                setValue={percentage.setValue}
                size="sm"
                trailingActions="%"
                value={percentage.value}
              />
            </>
          )}
        </>
      )}
    </Flex>
  );
});
